import React from "react";
import { Link, useParams } from "react-router-dom";
import useSEO from "../../hooks/useSEO";
import Breadcrumbs from "../../Breadcrumbs/Breadcrumbs.js";

import consultations from "./consultationsData.js";
import specialistsData from "../../Data/specialists/specialistsData.json";
import receptionSchedule from "../../Data/specialists/receptionSchedule.json";

export default function ConsultationPage() {
  const { slug } = useParams();

  const service = consultations.find((item) => item.slug === slug);

  const isMaterialCollection =
    service?.slug === "zabor-materiala-dlya-issledovaniya";

  useSEO(
    service
      ? {
          title: service.titleSeo,
          description: service.description,
          keywords: [
            service.specialty,
            service.title,
            "Екатеринбург",
            "УрНИИДВиИ",
            "стоимость медицинских услуг",
          ]
            .filter(Boolean)
            .join(", "),
          url: `/services/${service.slug}`,
        }
      : {
          title: "Медицинская услуга не найдена | УрНИИДВиИ",
          description:
            "Запрашиваемая медицинская услуга не найдена. Посмотрите список медицинских услуг УрНИИДВиИ в Екатеринбурге.",
          url: `/services/${slug || ""}`,
          noindex: true,
        },
  );

  if (!service) {
    return (
      <main className="page">
        <div className="container">
          <Breadcrumbs />

          <section className="section">
            <h1 className="section__title">Медицинская услуга не найдена</h1>

            <p>
              Возможно, адрес страницы изменился или услуга больше не
              представлена по этой ссылке. Вы можете посмотреть список
              медицинских услуг и действующий прейскурант УрНИИДВиИ.
            </p>

            <p>
              <Link to="/services">Все медицинские услуги</Link>
            </p>

            <p>
              <Link to="/patients/price-list">
                Прейскурант медицинских услуг
              </Link>
            </p>
          </section>
        </div>
      </main>
    );
  }

  /*
   * Получаем врачей из графика приёма.
   *
   * В consultationsData.js должно быть:
   * scheduleSection: "Дерматология взрослая"
   *
   * Название должно совпадать с полем section
   * в receptionSchedule.json.
   */

  /*
   * Получаем врачей из графика приёма и фильтруем
   * по типу услуги.
   */

  const scheduleSections = Array.isArray(service.scheduleSections)
    ? service.scheduleSections
    : service.scheduleSection
      ? Array.isArray(service.scheduleSection)
        ? service.scheduleSection
        : [service.scheduleSection]
      : [];

  const scheduledDoctors = scheduleSections.flatMap((sectionName) => {
    const group = receptionSchedule.find(
      (item) => item.section?.trim() === sectionName.trim(),
    );

    return group?.doctors || [];
  });

  // Убираем дубликаты врачей по slug.
  const uniqueScheduledDoctors = Array.from(
    new Map(
      scheduledDoctors
        .filter((doctor) => doctor?.slug)
        .map((doctor) => [doctor.slug, doctor]),
    ).values(),
  );

  // Находим данные врачей.
  let doctors = uniqueScheduledDoctors
    .map((scheduledDoctor) => {
      const specialist = specialistsData.find(
        (item) => item.slug === scheduledDoctor.slug,
      );

      if (!specialist) return null;

      return {
        ...specialist,
        schedule: scheduledDoctor,
      };
    })
    .filter(Boolean);

  /*
   * Фильтрация по типу услуги.
   *
   * Для страниц с доктором медицинских наук:
   * abbreviated: ", д.м.н."
   *
   * Для страниц с высшей категорией:
   * category: "Высшая категория"
   *
   * Остальные услуги показывают врачей по расписанию.
   */

  const normalizedTitle =
    `${service.h1 || ""} ${service.title || ""} ${service.titleSeo || ""}`.toLowerCase();

  const isDoctorOfMedicalSciencesPage =
    /доктор[а-яё\s]*медицинских наук|д\.?\s*м\.?\s*н\.?/i.test(normalizedTitle);
  const isHighestCategoryPage = /высшей категории/i.test(normalizedTitle);
  const isOrdinaryDermatologistPage =
    /дерматовенеролог/i.test(normalizedTitle) &&
    !isDoctorOfMedicalSciencesPage &&
    !isHighestCategoryPage;

  if (isDoctorOfMedicalSciencesPage) {
    // Только доктора медицинских наук.
    doctors = doctors.filter((doctor) =>
      /д\.?\s*м\.?\s*н\.?/i.test(doctor.abbreviated || ""),
    );
  } else if (isHighestCategoryPage) {
    // Только врачи высшей категории.
    doctors = doctors.filter(
      (doctor) => doctor.category?.trim() === "Высшая категория",
    );
  } else if (isOrdinaryDermatologistPage) {
    // Врачи без категории, у которых либо нет степени, либо степень — "Младший научный сотрудник".
    doctors = doctors.filter((doctor) => {
      const hasNoCategory = !doctor.category?.trim();

      const degree = doctor.degree?.trim();
      // Разрешаем пустую степень ИЛИ строго "Младший научный сотрудник"
      const isValidDegree = !degree || degree === "Младший научный сотрудник";

      // Дополнительно проверяем, чтобы в сокращениях не было КМН или ДМН
      const isNotMedicalScientist =
        !/д\.?\s*м\.?\s*н\.?|к\.?\s*м\.?\s*н\.?/i.test(
          doctor.abbreviated || "",
        );

      return hasNoCategory && isValidDegree && isNotMedicalScientist;
    });
  }

  const price = new Intl.NumberFormat("ru-RU").format(service.price);

  return (
    <section className="contacts education">
      <Breadcrumbs />

      {/* Основная информация об услуге */}

      <h1 className="contacts__title history__title">{service.h1}</h1>

      <p className="dissertation__text">{service.content}</p>

      <div className="dissertation__text">
        <span>Стоимость услуги по прейскуранту: </span>
        <span> {price} ₽</span>
      </div>

      <p className="dissertation__text">{service.qualification}</p>

      <div class="breadcrumbs">
        <div className="allnews__blank-link">
          <Link
            className="prevention__button news__button licenses__toggle-btn"
            to="/#record"
          >
            {isMaterialCollection
              ? "Уточнить порядок записи"
              : "Записаться на приём"}
          </Link>
        </div>
      </div>
      {/* </section> */}

      {/* Информация о консультации */}
      <section className="section">
        <h2 className="about-nii__title antiCorruption__title">
          {isMaterialCollection
            ? "Забор материала для лабораторного исследования"
            : `Консультация врача: ${service.specialty}а`}
        </h2>

        {isMaterialCollection ? (
          <>
            <p className="dissertation__text">
              Забор материала проводится для последующего лабораторного или
              иного исследования в соответствии с назначением и медицинскими
              показаниями.
            </p>

            <p className="dissertation__text">
              Вид материала, способ его получения и требования к подготовке
              зависят от конкретного исследования. До посещения уточните у
              регистратуры, требуется ли предварительная консультация врача и
              специальная подготовка.
            </p>
          </>
        ) : (
          <>
            <p className="dissertation__text">
              Консультация врача {service.specialty}а в УрНИИДВиИ в
              Екатеринбурге предназначена для оценки состояния пациента с учётом
              жалоб, истории заболевания и предоставленных медицинских
              документов.
            </p>

            <p className="dissertation__text">
              Во время первичного обращения врач уточняет сведения о состоянии
              здоровья и определяет дальнейшую тактику в рамках своей
              специальности. При повторном обращении специалист может оценить
              результаты ранее назначенных обследований и динамику состояния
              пациента.
            </p>

            <p className="dissertation__text">
              Конкретный объём осмотра, необходимость дополнительных
              исследований и дальнейшие рекомендации определяются врачом
              индивидуально.
            </p>
          </>
        )}
      </section>

      {/* Врачи, автоматически полученные из графика */}
      <section className="section">
        <h2 className="about-nii__title antiCorruption__title">
          {isMaterialCollection
            ? "Специалисты, выполняющие услугу"
            : "Врачи, ведущие приём по этой услуге"}
        </h2>
        {doctors.length > 0 ? (
          <div className="staff-list">
            {doctors.map((doctor) => (
              <div className="staff-card" key={doctor.id || doctor.slug}>
                <div className="staff-card__photo">
                  <img src={doctor.photo} alt={doctor.name} loading="lazy" />
                </div>

                <div className="staff-card__info">
                  <h2>{doctor.name}</h2>

                  <p>{doctor.position}</p>

                  <Link
                    to={doctor.link || `/patients/specialists/${doctor.slug}`}
                    className="staff-card__link"
                  >
                    Подробнее →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="dissertation__text">
            Информация о специалистах, ведущих приём по этой услуге, уточняется
            в регистратуре УрНИИДВиИ.
          </p>
        )}
        <p className="dissertation__text">
          Список специалистов формируется на основании текущего графика приёма.
          Возможность записи к конкретному врачу необходимо уточнять на странице
          записи.
        </p>
        <div class="breadcrumbs">
          <div className="allnews__blank-link">
            <Link
              className="prevention__button news__button licenses__toggle-btn"
              to="/patients/reception"
            >
              Записаться на приём
            </Link>
          </div>
        </div>
      </section>

      {/* Стоимость услуги */}
      <section className="section">
        <h2 className="about-nii__title antiCorruption__title">
          Стоимость и информация об услуге
        </h2>

        <div class="breadcrumbs">
          <div className="allnews__blank-link">
            <div className="price-card">
              <h3 className="about-nii__title antiCorruption__title">
                {service.title}
              </h3>

              <p className="dissertation__text">{price} ₽</p>

              <p className="dissertation__text">Код услуги: {service.code}</p>

              <p className="dissertation__text">
                Специализация: {service.specialty}
              </p>

              <p className="dissertation__text">
                Условия оказания: {service.qualification}
              </p>
            </div>
          </div>
        </div>

        <p className="dissertation__text">
          Стоимость указана по прейскуранту. Перед записью рекомендуется
          уточнить актуальную цену, доступность услуги и условия её оказания в
          регистратуре УрНИИДВиИ.
        </p>
      </section>

      {/* Подготовка к посещению */}
      <section className="section">
        <h2 className="about-nii__title antiCorruption__title">
          Как подготовиться к посещению
        </h2>

        <ul className="list">
          <li className="privacy__text">
            Заранее уточните дату, время и условия записи.
          </li>
          <li className="privacy__text">
            При наличии возьмите с собой медицинские выписки, результаты
            предыдущих обследований и заключения специалистов, относящиеся к
            вашему обращению.
          </li>
          <li className="privacy__text">
            Уточните у регистратуры, необходима ли специальная подготовка к
            выбранной услуге.
          </li>
        </ul>

        <p className="dissertation__text">
          Если вы обращаетесь повторно, по возможности подготовьте результаты
          обследований и рекомендации, полученные ранее.
        </p>
      </section>

      {/* Запись */}
      <h2 className="about-nii__title antiCorruption__title">
        Запись на медицинскую услугу
      </h2>

      <p className="dissertation__text">
        Чтобы уточнить возможность получения услуги в УрНИИДВиИ в Екатеринбурге,
        перейдите на страницу записи. Там можно ознакомиться с порядком
        обращения и уточнить доступность приёма у нужного специалиста.
      </p>

      <div class="breadcrumbs">
        <div className="allnews__blank-link">
          <Link
            className="prevention__button news__button licenses__toggle-btn"
            to="/patients/reception"
          >
            {isMaterialCollection
              ? "Уточнить запись на услугу"
              : `Записаться к врачу: ${service.specialty}`}
          </Link>
        </div>
      </div>

      {/* Связанные разделы */}
      <nav
        className="section service-related"
        aria-label="Связанные разделы сайта"
      >
        <h2 className="about-nii__title antiCorruption__title">
          Полезная информация
        </h2>

        <ul className="list">
          <li className="privacy__text">
            <Link className="staff-card__link" to="/services">
              Все медицинские услуги УрНИИДВиИ
            </Link>
          </li>
          <li className="privacy__text">
            <Link className="staff-card__link" to="/patients/price-list">
              Полный прейскурант медицинских услуг
            </Link>
          </li>
          <li className="privacy__text">
            <Link className="staff-card__link" to="/patients/specialists">
              Врачи и специалисты института
            </Link>
          </li>
          <li className="privacy__text">
            <Link className="staff-card__link" to="/#record">
              Запись на приём к специалистам
            </Link>
          </li>
          <li className="privacy__text">
            <Link className="staff-card__link" to="/contacts">
              Контакты, адрес и схема проезда
            </Link>
          </li>
        </ul>
      </nav>

      {/* Медицинская информация */}
      <section className="section">
        <h2 className="about-nii__title antiCorruption__title">
          Важная информация
        </h2>

        <p className="dissertation__text">
          Информация на странице носит справочный характер и не заменяет
          консультацию медицинского специалиста. Необходимость медицинского
          вмешательства и его объём определяются с учётом индивидуальной
          ситуации пациента.
        </p>
      </section>
    </section>
  );
}
